import { Badge } from "@/components/ui/badge";
import { formatPlanPrice, formatPlanStorageLimit } from "@/lib/billingPlanDisplay";
import { formatStorageBytes } from "@/lib/formatStorageBytes";
import type { BillingPurchasablePricePlan } from "@/types/billing";

type Props = {
  plan: BillingPurchasablePricePlan;
  selected: boolean;
  isCurrent?: boolean;
  onSelect: (airwallexPriceId: string) => void;
};

const blockMessage = (plan: BillingPurchasablePricePlan): string | null => {
  if (plan.purchasable !== false) {
    return null;
  }
  if (plan.block_reason === "storage_usage_exceeds_plan") {
    const used = formatStorageBytes(plan.used_bucket_bytes);
    const limit = formatStorageBytes(plan.proposed_limit_bytes);
    if (used && limit) {
      return `Your stored data (${used}) exceeds this plan's limit (${limit}). Free up space or add storage first.`;
    }
    return "Your stored data exceeds this plan's limit. Free up space or add storage first.";
  }
  return "This plan is not available for your account right now.";
};

/** One selectable plan. Shared by the Billing page and the credit gate modal so copy cannot drift. */
export const BillingPlanOptionCard = ({
  plan,
  selected,
  isCurrent = false,
  onSelect,
}: Props) => {
  const priceLabel = formatPlanPrice(plan.price, plan.period, plan.currency);
  const blocked = blockMessage(plan);
  const disabled = Boolean(blocked) || isCurrent;

  return (
    <button
      type="button"
      className={`rounded-lg border p-4 text-left transition-colors ${
        disabled
          ? isCurrent
            ? "border-primary/40 bg-primary/5"
            : "cursor-not-allowed border-dashed opacity-60"
          : selected
            ? "border-primary bg-primary/5"
            : "hover:border-primary/50"
      }`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onSelect(plan.airwallex_price_id)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium">{plan.name}</div>
        {isCurrent ? (
          <Badge variant="secondary" className="shrink-0">
            Current plan
          </Badge>
        ) : null}
      </div>
      {priceLabel ? (
        <div className="mt-1 text-sm font-medium">{priceLabel}</div>
      ) : null}
      <div className="mt-1 text-sm text-muted-foreground">
        {Number(plan.credits).toLocaleString()} credits per {plan.period}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">
        {formatPlanStorageLimit(plan.storage_limit_gb)}
      </div>
      {blocked ? (
        <div className="mt-2 text-sm text-destructive">{blocked}</div>
      ) : null}
    </button>
  );
};

export default BillingPlanOptionCard;
